import React, { useEffect, useState } from "react";

import App from "../Global/App";
import Page from "../Components/Page";
import State from "../Global/State";
import Settings from "../Global/Settings";
import Panel from "../Components/Panel";
import { useAuth } from "../Global/Auth";
import Formatter from "../Global/Formatter";
import CopyrightDisclaimer from "./Login/CopyrightDisclaimer";

import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CampaignIcon from "@mui/icons-material/Campaign";
import { useLocation, useNavigate } from "react-router";

// This implements OAuth using a Token Mediating Backend.
// https://www.ietf.org/archive/id/draft-ietf-oauth-browser-based-apps-11.html#name-token-mediating-backend
//
// This page is intended to be redirected to from the OAuth provider.
//
// It VIOLATES the following SHOULD-level requirements of the specification:
// - "... the connection between the browser and Token Mediating Backend SHOULD be a session cookie provided by the backend"
//   There is no session cookie. What prevents anyone from hitting the backend for tokens? How does this need to be locked down, and how can it be?
// - "The frontend SHOULD NOT persist tokens in local storage or similar mechanisms; instead, the frontend SHOULD store tokens only in memory, and make a new request to the backend if no tokens exist"
//   Tokens are persisted in (secure on mobile) local storage, not memory. This matches what the frontend already does. This increases XSS attack risk.
// - "The Token Mediating Backend SHOULD cache tokens it obtains from the authorization server ..."
//   No such caching is done. This would yield worse performance for the prior requirement, but as it is violated this doesn't matter.

async function loginWithHelixRedirect(loginWithAuthorization, query) {
    const route = "auth/truehelix/redirect";
    const url = new URL(`${Settings.apiPath}/${route}`);
    url.search = query;
    const response = await fetch(url, { credentials: "include" });

    // Based on API.js
    if (!response) {
        // eslint-disable-next-line no-throw-literal
        throw 'An unexpected error occurred.';
    }
    if (response && response.status === 'error') {
        throw response.message;
    }
    const json = await response.json();

    await loginWithAuthorization(json);
}

/**
 * @returns {*}
 * @constructor
 */
const LoginTrueHelixRedirect = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const { authenticated, loginWithAuthorization } = useAuth();
    const [isLoading, setLoading] = useState(false);

    /**
     * If the user is already authenticated, redirect to the dashboard.
     * Otherwise, invoke the backend to acquire a token.
     */
    useEffect(() => {
        if (authenticated) {
            navigate("/");
            return;
        }

        setLoading(true);
        
        const searchParams = new URLSearchParams(window.location.search);
        loginWithHelixRedirect(loginWithAuthorization, searchParams)
            .then(() => {
                // Need to do a hard load here to refresh the top navigation, otherwise
                // the user will be directed elsewhere in the site without menu items.
                //
                navigate(state && state.path ? state.path : "/");
            })
            .catch((message) => {
                setLoading(false);
                setError(message);
            });
    }, [authenticated, navigate]);

    return (
        <Page hideHeader>
            <Box className={"columns__1"}>
                <Panel
                    header={
                        <Box
                            className={"text__center"}
                            children={
                                <img
                                    alt={"Login logo"}
                                    src={Formatter.url(
                                        "assets/images/logo.png",
                                    )}
                                    style={{ width: "70%" }}
                                />
                            }
                        />
                    }
                    padding={0}
                    className={"login__block"}
                    hideDivider
                >
                    <form autoComplete="on">
                        <Box
                            className={"columns__1"}
                            sx={{ padding: "1.5em", paddingTop: 0 }}
                        >
                            {error && (
                                <Alert color={"error"} children={error} />
                            )}

                            {!error && (
                                <div>
                                    Logging in with True Helix...
                                </div>
                            )}

                            <CopyrightDisclaimer />
                        </Box>
                    </form>
                </Panel>
            </Box>
        </Page>
    );
};

export default LoginTrueHelixRedirect;
